import React, { useRef, useState, useEffect } from 'react';
import './ShareableTranscriptCard.css';

/**
 * ShareableTranscriptCard - A visually appealing card for sharing transcripts
 * Similar to Spotify's share cards or Airchat's shareable content
 * Supports audio playback when audioUrl is provided
 */
function ShareableTranscriptCard({
  transcriptText,
  transcriptName,
  duration,
  shareUrl,
  audioUrl, // Optional: if provided, enables audio playback
  onClose,
  onCopyLink,
  theme = 'gradient-blue' // gradient-blue, gradient-purple, gradient-sunset, dark, light
}) {
  const cardRef = useRef(null);
  const audioRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);

  // Get a clean excerpt from the transcript
  const getExcerpt = (text, maxLength = 280) => {
    if (!text) return '';
    // Remove extra whitespace and newlines
    const cleaned = text.replace(/\s+/g, ' ').trim();
    if (cleaned.length <= maxLength) return cleaned;
    // Cut at word boundary
    const truncated = cleaned.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    return truncated.substring(0, lastSpace) + '...';
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60);
      const remainMins = mins % 60;
      return `${hrs}:${remainMins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate random waveform bars for visual effect
  const generateWaveformBars = (count = 40) => {
    const bars = [];
    for (let i = 0; i < count; i++) {
      // Create a wave-like pattern
      const position = i / count;
      const baseHeight = Math.sin(position * Math.PI) * 0.6 + 0.2;
      const randomVariation = (Math.random() - 0.5) * 0.3;
      const height = Math.max(0.1, Math.min(1, baseHeight + randomVariation));
      bars.push(height);
    }
    return bars;
  };

  const [waveformBars] = useState(() => generateWaveformBars());

  // Audio playback controls
  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleWaveformClick = (e) => {
    if (!audioRef.current || !audioUrl) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * audioDuration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);

    if (!isPlaying) {
      audioRef.current.play();
    }
  };

  // Calculate progress percentage for waveform
  const progressPercentage = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  // Copy share link
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (onCopyLink) onCopyLink();
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.classList.contains('share-card-overlay')) {
        onClose?.();
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const excerpt = getExcerpt(transcriptText);
  const formattedDuration = formatDuration(duration);

  return (
    <div className="share-card-overlay">
      <div className="share-card-container">
        <button className="share-card-close" onClick={onClose} aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* The actual shareable card */}
        <div ref={cardRef} className={`share-card theme-${theme}`}>
          {/* Background pattern */}
          <div className="share-card-bg-pattern"></div>

          {/* Content */}
          <div className="share-card-content">
            {/* Header with branding */}
            <div className="share-card-header">
              <div className="share-card-logo">
                <div className="logo-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                  </svg>
                </div>
                <span className="logo-text">Rec-Chord</span>
              </div>
              {formattedDuration && (
                <div className="share-card-duration">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{formattedDuration}</span>
                </div>
              )}
            </div>

            {/* Audio element (hidden) */}
            {audioUrl && (
              <audio
                ref={audioRef}
                src={audioUrl}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => {
                  setIsPlaying(false);
                  setCurrentTime(0);
                }}
                preload="metadata"
              />
            )}

            {/* Waveform visualization with playback */}
            <div
              className={`share-card-waveform ${audioUrl ? 'has-audio' : ''}`}
              onClick={audioUrl ? handleWaveformClick : undefined}
              style={{ '--progress': `${progressPercentage}%` }}
            >
              {/* Play/Pause button overlay */}
              {audioUrl && (
                <button
                  className={`waveform-play-btn ${isPlaying ? 'playing' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePlayPause();
                  }}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path fillRule="evenodd" d="M6.75 5.25a.75.75 0 01.75-.75H9a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H7.5a.75.75 0 01-.75-.75V5.25zm7.5 0A.75.75 0 0115 4.5h1.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H15a.75.75 0 01-.75-.75V5.25z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path fillRule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              )}

              {/* Waveform bars */}
              <div className="waveform-bars-container">
                {waveformBars.map((height, index) => {
                  const barPosition = (index / waveformBars.length) * 100;
                  const isPlayed = barPosition < progressPercentage;
                  return (
                    <div
                      key={index}
                      className={`waveform-bar ${isPlayed ? 'played' : ''}`}
                      style={{ '--bar-height': `${height * 100}%` }}
                    />
                  );
                })}
              </div>

              {/* Time display */}
              {audioUrl && audioDuration > 0 && (
                <div className="waveform-time">
                  <span>{formatDuration(currentTime)}</span>
                  <span>/</span>
                  <span>{formatDuration(audioDuration)}</span>
                </div>
              )}
            </div>

            {/* Transcript excerpt */}
            <div className="share-card-text">
              <p>"{excerpt}"</p>
            </div>

            {/* Title */}
            {transcriptName && (
              <div className="share-card-title">
                <h3>{transcriptName}</h3>
              </div>
            )}

            {/* Footer with CTA */}
            <div className="share-card-footer">
              <div className="share-card-cta">
                <span className="cta-text">Try</span>
                <span className="cta-brand">Rec-Chord</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="cta-arrow">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="share-card-actions">
          <div className="theme-selector">
            <span className="theme-label">Style:</span>
            <div className="theme-options">
              <button
                className={`theme-option theme-gradient-blue ${theme === 'gradient-blue' ? 'active' : ''}`}
                onClick={() => window.dispatchEvent(new CustomEvent('shareCardThemeChange', { detail: 'gradient-blue' }))}
                title="Blue Gradient"
              />
              <button
                className={`theme-option theme-gradient-purple ${theme === 'gradient-purple' ? 'active' : ''}`}
                onClick={() => window.dispatchEvent(new CustomEvent('shareCardThemeChange', { detail: 'gradient-purple' }))}
                title="Purple Gradient"
              />
              <button
                className={`theme-option theme-gradient-sunset ${theme === 'gradient-sunset' ? 'active' : ''}`}
                onClick={() => window.dispatchEvent(new CustomEvent('shareCardThemeChange', { detail: 'gradient-sunset' }))}
                title="Sunset Gradient"
              />
              <button
                className={`theme-option theme-dark ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => window.dispatchEvent(new CustomEvent('shareCardThemeChange', { detail: 'dark' }))}
                title="Dark"
              />
              <button
                className={`theme-option theme-light ${theme === 'light' ? 'active' : ''}`}
                onClick={() => window.dispatchEvent(new CustomEvent('shareCardThemeChange', { detail: 'light' }))}
                title="Light"
              />
            </div>
          </div>

          <div className="action-buttons">
            <button
              className="share-action-btn copy-btn"
              onClick={handleCopyLink}
              disabled={!shareUrl}
            >
              {copied ? (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                  </svg>
                  Copy Link
                </>
              )}
            </button>
          </div>
        </div>

        {/* Share URL display */}
        {shareUrl && (
          <div className="share-url-container">
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="share-url-input"
              onClick={(e) => e.target.select()}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ShareableTranscriptCard;
